import {Zippable, zipSync} from 'fflate';

import {
    generateAttachmentLink, 
    generateSignatureLink, 
    generateAttachmentLinkWithContentType
} from 'components/table/columns/file/file-column-utils';
import {ECommonMessages} from 'shared/constants/messages';
import {EFileType} from 'shared/types/files';
import {downloadFile} from 'shared/utils/request';
import {showMessageFromResponse} from 'shared/utils/show-message-from-response';

/**
 * Функция для скачивания файла по его идентификатору.
 *
 * @param {string} filename - Имя файла, которое будет использоваться при скачивании.
 * @param {string} attachmentId - Идентификатор вложения (файла).
 * @param {string} entityName - Имя сущности страницы, к которой относится файл (берется из url).
 *
 * @description
 * Функция выполняет запрос на скачивание файла по указанному `attachmentId` и `entityName`.
 * Если файл успешно скачан (статус ответа 200), отображается сообщение об успешной загрузке.
 * В случае ошибки отображается соответствующее сообщение.
 */
export const downloadFileById = (filename: string, attachmentId: string, entityName: string) => {
    downloadFile(`${entityName || ''}/attachment/${attachmentId}`, filename)
        .then(data => {
            if (data.status === 200) {
                showMessageFromResponse({
                    response: data,
                    isError: false,
                    customMessage: ECommonMessages.UPLOAD_FILE_SUCCESS,
                });
            }
        })
        .catch(error => {
            showMessageFromResponse({response: error?.response, isError: true});
        });
};

/**
 * Функция для скачивания подписи по её идентификатору.
 *
 * @param {string} filename - Имя файла, которое будет использоваться при скачивании.
 * @param {string} signId - Идентификатор подписи.
 * @param {string} entityName - Имя сущности страницы, к которой относится файл (берется из url).
 *
 * @description
 * Функция выполняет запрос на скачивание подписи по указанному `signId` и `entityName`.
 * Если подпись успешно скачана (статус ответа 200), отображается сообщение об успешной загрузке.
 * В случае ошибки отображается соответствующее сообщение.
 */
export const downloadSignatureById = (filename: string, signId: string, entityName: string) => {
    downloadFile(`${entityName}/signature/${signId}`, filename)
        .then(data => {
            if (data.status === 200) {
                showMessageFromResponse({
                    response: data,
                    isError: false,
                    customMessage: ECommonMessages.UPLOAD_SIGNATURE_SUCCESS,
                });
            }
        })
        .catch(error => {
            showMessageFromResponse({response: error?.response, isError: true});
        });
};

interface IGenerateUrlByFileTypeArgs {
    id: string | number;
    type: EFileType;
    entityName: string;
    stringStructure?: string;
}

/**
 * Функция для генерации URL в зависимости от типа файла.
 *
 * @param {IGenerateUrlByFileTypeArgs} args - Аргументы для генерации URL.
 * @param {string | number} args.id - Идентификатор файла или подписи.
 * @param {EFileType} args.type - Тип файла (например, `FileType.FILE` или `FileType.SIGNATURE`).
 * @param {string} args.entityName - Имя сущности страницы, к которой относится файл (берется из url).
 * @param {string} args.stringStructure - параметр из меты.
 * @returns {string | null} - Сгенерированный URL или `null`, если тип файла не поддерживается.
 *
 * @description
 * Функция генерирует URL для скачивания файла или подписи в зависимости от переданного типа (`FileType`).
 * Если тип файла не поддерживается, возвращается `null`.
 * Для additionalFileList передаётся добалвяется query параметр contentType - для корреткного скачивания PDF файлов.
 */
export const generateUrlByFileType = ({id, type, entityName, stringStructure}: IGenerateUrlByFileTypeArgs) => {
    switch (type) {
    case EFileType.FILE:
        if (stringStructure === 'additionalFileList') {
            return generateAttachmentLinkWithContentType({attachmentId: id, entityName, contentType: 'PDF_DOCUMENT'});
        }

        return generateAttachmentLink({attachmentId: id, entityName});
    case EFileType.SIGNATURE:
        return generateSignatureLink({attachmentId: id, entityName});
    default:
        return null;
    }
};

/**
 * Заменяет расширение файла на .zip
 * @param {string} filename - Имя файла, у которого нужно заменить расширение
 * @returns {string} Имя файла с расширением .zip
 */
export const replaceExtensionToZip = (filename: string) => filename.replace(/\.[^/.]+$/, '.zip');

/**
 * Сжимает файл в zip архив
 * @param {Object} params - Параметры для сжатия
 * @param {string} params.filename - Имя файла
 * @param {File} params.file - Файл для сжатия
 * @param {number} [compressionLevel=3] - Уровень сжатия (от 0 до 9)
 * @returns {Promise<{ filename: string, file: File | Blob, success: boolean, error?: unknown }>}
 * Объект с именем сжатого файла, самим файлом и флагом успеха
 */
export const compressFile = async (
    {filename, file}: { filename: string; file: File },
    compressionLevel: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 = 3,
): Promise<{
    filename: string;
    file: File | Blob;
    success: boolean;
    error?: unknown;
  }> => {
    if (file?.type === 'application/zip') {
        return {filename, file, success: true};
    }

    try {
        const fileData = new Uint8Array(await file.arrayBuffer());

        const input: Zippable = {
            [filename]: [fileData, {level: compressionLevel}],
        };

        const zipped = zipSync(input, {level: compressionLevel});
        const zipBlob = new Blob([zipped], {type: 'application/zip'});

        return {
            filename: replaceExtensionToZip(filename),
            file: zipBlob,
            success: true,
        };
    } catch (error) {
        console.error('Ошибка при сжатии файла:', error);
        return {
            filename, file, success: false, error,
        };
    }
};
